import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Grid } from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import ExpertCard from 'src/pages/QueryPages/QueryProfile/components/ExpertSelectionCard';
import { useTabManagement } from 'src/hooks/useTabManagement';
import ExpertDirectoryModal from 'src/pages/QueryPages/QueryProfile/components/ExpertDirectoryModal';

import RacoonLoading from 'src/components/RacoonLoading';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { queryApi } from 'src/api/queryApi';

const findExpertById = (experts, expertId) => experts?.find((expert) => Number(expert?.id) === Number(expertId));

const ExpertSelectionTab = ({ query, experts, refetch, selectedExpert, setSelectedExpert, loading, handleExpertDirectoryClick }) => {
  const { modalOpen, handleModalClose, handleModalOpen } = useTabManagement();
  const { decodedToken } = useDecodedToken();

  const updateExpertID = async (expertID) => {
    try {
      const queriesResponse = await queryApi.updateQuery(query.id, query.status, decodedToken?.userID, undefined, expertID);

      return queriesResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating expert ID:', error);
      throw error; // Re-throw to ensure the error is propagated
    }
  };

  const handleExpertSelect = async (expertId) => {
    handleModalClose();

    // Find the selected expert from the experts array
    const expert = findExpertById(experts, expertId);

    if (!expert) {
      // eslint-disable-next-line no-console
      console.error('Expert not found:', expertId);
      return;
    }

    if (query?.status === 'draft') {
      const success = await handleExpertDirectoryClick(expertId);
      if (success) {
        await updateExpertID(expertId);
        setSelectedExpert(expert); // Update the selected expert immediately
        refetch();
      }
    } else {
      await updateExpertID(expertId);
      setSelectedExpert(expert); // Update the selected expert immediately
      refetch();
    }
  };

  return (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
          <RacoonLoading />
        </Box>
      ) : query?.status === 'draft' ? (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {query?.leads?.map((lead) => {
              const expert = findExpertById(experts, lead.expertID);
              return (
                expert && (
                  <Grid item xs={12} sm={6} md={6} key={expert.id}>
                    <ExpertCard
                      avatar={expert?.imageURL}
                      name={expert?.name}
                      positionAndCompany={expert?.position + ' at ' + expert?.company}
                      onClick={() => setSelectedExpert(expert)}
                      isActive={Number(selectedExpert?.id) === Number(expert?.id)}
                    />
                  </Grid>
                )
              );
            })}
            <Grid item xs={12} sm={6} md={6}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  mt: { xs: 0, sm: 1 },
                  height: { xs: '100px', sm: '134px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
                onClick={handleModalOpen}
              >
                <AccountCircle sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mr: 1 }} />
                Expert Directory
              </Button>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
            Please select an expert from the directory below.
          </Typography>
          <Button variant="contained" onClick={handleModalOpen}>
            Choose a different expert
          </Button>

          {selectedExpert && (
            <ExpertCard
              avatar={selectedExpert?.imageURL}
              name={selectedExpert?.name}
              positionAndCompany={selectedExpert?.position + ' at ' + selectedExpert?.company}
              onClick={() => setSelectedExpert(selectedExpert)}
              isActive={false}
            />
          )}
        </Box>
      )}
      <ExpertDirectoryModal open={modalOpen} onClose={handleModalClose} experts={experts} onExpertSelect={handleExpertSelect} />
    </>
  );
};

ExpertSelectionTab.propTypes = {
  selectedExpert: PropTypes.object,
  setSelectedExpert: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  handleExpertDirectoryClick: PropTypes.func.isRequired,
  query: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    leads: PropTypes.array,
  }).isRequired,
  experts: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default ExpertSelectionTab;
